import * as React from 'react';
import { Alert, Button, ButtonGroup, Spinner, Table } from 'reactstrap';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { asPage } from '../../utils/wrappers/asPage';
import { LProps, withLocalization } from '../../utils/wrappers';
import { State } from '../../redux/state';
import * as selectors from '../../redux/selectors';
import { userApiBasePath } from '../../redux/selectors/userApi';
import Footer from '../../shared/Footer';
import { CompetitionPledges } from '../../userApi';
import { estimateEntityDollarSavings } from '../../utils/actionSavings';
import HourlyPointsChart, { HourlyPointsBlock } from '../../shared/HourlyPointsChart';
import '../../shared/hourlyPointsChart.scss';
import './totals.scss';

interface OrgRow {
  department: string;
  division?: string;
  participants: number;
  activeParticipants: number;
  actionsCompleted: number;
  newActions?: number;
  existingActions?: number;
  totalPoints: number;
  avgPointsPerParticipant: number;
  avgActionsPerParticipant: number;
  co2Lbs?: number;
  newCO2Lbs?: number;
  existingCO2Lbs?: number;
  waterGallons?: number;
  rank?: number;
}

interface CO2SavingsRow {
  department?: string;
  actionId?: number;
  title?: string;
  category?: string;
  newCompletions: number;
  alreadyDoCompletions: number;
  newCO2Lbs: number;
  alreadyDoCO2Lbs: number;
  totalCO2Lbs: number;
}

interface UserRow {
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
  status: string;
  level: string;
  verified: boolean;
  preregistered: boolean;
  department: string;
  division: string;
  divisionEntityId?: number;
  teamName: string;
  actionsCompleted: number;
  newActions?: number;
  existingActions?: number;
  totalPoints: number;
  co2Lbs: number;
  waterGallons?: number;
  stories: number;
  comments: number;
  heroNominationsGiven: number;
  heroNominationsReceived: number;
  flagsFiled: number;
  flagsReceived: number;
  joinedAt?: string;
  registeredAt?: string;
}

interface TeamOption {
  entityId: number;
  department: string;
  division: string;
  name: string;
}

interface UserEditDraft {
  email: string;
  status: string;
  department: string;
  divisionEntityId: number;
}

interface TotalsReport {
  publicName: string;
  elapsedMs: number;
  queryCount: number;
  definitions: { [key: string]: string };
  labels: { campusSingular: string; teamSingular: string; teamPlural: string };
  summary: {
    totalParticipants: number;
    preregisteredParticipants?: number;
    preregisteredPendingJoin?: number;
    activeParticipants: number;
    participantsWithZeroActions: number;
    totalActionsCompleted: number;
    uniqueActionsCompleted: number;
    totalPoints: number;
    avgPointsPerParticipant: number;
    avgActionsPerParticipant: number;
    participatingDepartments: number;
    participatingDivisions: number;
    totalCO2Lbs: number;
    totalElectricityKwh: number;
    totalGasGallons: number;
  };
  activityBuckets: Array<{ label: string; count: number; percent: number; display: string }>;
  departments: OrgRow[];
  divisions: OrgRow[];
  popularActions: Array<{
    title: string; category: string; timesCompleted: number;
    uniqueParticipants: number; totalPointsAwarded: number;
  }>;
  leastUsedActiveActions: Array<{
    title: string; category: string; timesCompleted: number;
    uniqueParticipants: number; totalPointsAwarded: number;
  }>;
  dailyActivity: Array<{ day: string; actions: number; points: number }>;
  hourlyPointsTimeSeries?: HourlyPointsBlock;
  co2SavingsSummary: {
    newCompletions: number;
    alreadyDoCompletions: number;
    newCO2Lbs: number;
    alreadyDoCO2Lbs: number;
    totalCO2Lbs: number;
  };
  co2SavingsByDepartment: CO2SavingsRow[];
  co2SavingsByAction: CO2SavingsRow[];
  users: UserRow[];
  teamOptions?: TeamOption[];
  reconciliation: {
    pointsMatch: boolean;
    actionsMatch: boolean;
    participantsMatch: boolean;
    divisionSumMatchesLedger: boolean;
    notes: string[];
    queryCount?: number;
  };
}

interface Props {
  apiBase: string;
  token: string;
  competitionName: string;
  canManage: boolean;
  competitionPledges?: CompetitionPledges;
}

interface LocalState {
  loading: boolean;
  error: string;
  report: TotalsReport | null;
  deptSort: string;
  divSort: string;
  userSort: string;
  co2View: 'department' | 'action';
  pointsSeriesView: 'department' | 'division';
  editingKey: string;
  editDraft: UserEditDraft | null;
  saving: boolean;
  saveError: string;
}

const csvEscape = (value: string | number) => {
  const s = String(value == null ? '' : value);
  if (/[",\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const downloadCsv = (filename: string, headers: string[], rows: Array<Array<string | number>>) => {
  const lines = [headers.map(csvEscape).join(',')].concat(
    rows.map((row) => row.map(csvEscape).join(','))
  );
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const sortRows = <T extends object>(rows: T[], key: string) => {
  const desc = key.charAt(0) !== '+';
  const field = desc ? key : key.slice(1);
  const copy = rows.slice();
  copy.sort((a, b) => {
    const av = (a as any)[field];
    const bv = (b as any)[field];
    if (typeof av === 'number' && typeof bv === 'number') {
      return desc ? bv - av : av - bv;
    }
    if (typeof av === 'boolean' && typeof bv === 'boolean') {
      const an = av ? 1 : 0;
      const bn = bv ? 1 : 0;
      return desc ? bn - an : an - bn;
    }
    const cmp = String(av || '').localeCompare(String(bv || ''));
    return desc ? -cmp : cmp;
  });
  return copy;
};

class TotalsPage extends React.Component<LProps<Props>, LocalState> {
  public state: LocalState = {
    loading: true,
    error: '',
    report: null,
    deptSort: 'totalPoints',
    divSort: 'totalPoints',
    userSort: 'totalPoints',
    co2View: 'department',
    pointsSeriesView: 'department',
    editingKey: '',
    editDraft: null,
    saving: false,
    saveError: ''
  };

  public componentDidMount() {
    this.load();
  }

  private load = async () => {
    const { apiBase, token, competitionName, canManage } = this.props;
    if (!canManage) {
      this.setState({ loading: false, error: 'Admin access required.' });
      return;
    }
    this.setState({ loading: true, error: '' });
    try {
      const response = await fetch(`${apiBase}/competitions/${competitionName}/admin/totals`, {
        headers: { Authorization: token, Accept: 'application/json' }
      });
      if (response.status === 401 || response.status === 403) {
        this.setState({ loading: false, error: 'Admin access required.' });
        return;
      }
      if (!response.ok) {
        this.setState({ loading: false, error: 'Could not load totals.' });
        return;
      }
      const report = await response.json();
      this.setState({ loading: false, report });
    } catch (_err) {
      this.setState({ loading: false, error: 'Network error loading totals.' });
    }
  }

  private moneySavedForCO2 = (co2Lbs?: number) => {
    const dollars = estimateEntityDollarSavings(
      { savingsCO2Lbs: co2Lbs || 0 },
      this.props.competitionPledges
    );
    return Math.round(dollars * 100) / 100;
  }

  private exportDepartments = () => {
    const { report } = this.state;
    if (!report) return;
    downloadCsv('department-totals.csv', [
      'Department', 'Participants',
      'New Actions', 'Existing Actions', 'Total Actions-to-Date',
      'Total Points', 'New Actions CO2e lbs', 'Existing Actions CO2e lbs', 'Total Actions CO2e lbs',
      'Total Actions Water (gal)', 'Total Actions $ Saved'
    ], sortRows(report.departments || [], this.state.deptSort).map((r) => [
      r.department, r.participants,
      r.newActions || 0, r.existingActions || 0, r.actionsCompleted,
      r.totalPoints, r.newCO2Lbs || 0, r.existingCO2Lbs || 0, r.co2Lbs || 0,
      r.waterGallons || 0, this.moneySavedForCO2(r.co2Lbs)
    ]));
  }

  private exportDivisions = () => {
    const { report } = this.state;
    if (!report) return;
    downloadCsv('division-totals.csv', [
      'Department', 'Division', 'Participants',
      'New Actions', 'Existing Actions', 'Total Actions-to-Date',
      'Total Points', 'New Actions CO2e lbs', 'Existing Actions CO2e lbs', 'Total Actions CO2e lbs',
      'Total Actions Water (gal)', 'Total Actions $ Saved'
    ], sortRows(report.divisions || [], this.state.divSort).map((r) => [
      r.department, r.division || '', r.participants,
      r.newActions || 0, r.existingActions || 0, r.actionsCompleted,
      r.totalPoints, r.newCO2Lbs || 0, r.existingCO2Lbs || 0, r.co2Lbs || 0,
      r.waterGallons || 0, this.moneySavedForCO2(r.co2Lbs)
    ]));
  }

  private exportUsers = () => {
    const { report } = this.state;
    if (!report) return;
    const teamLabel = (report.labels && report.labels.teamSingular) || 'Division';
    downloadCsv('user-summary.csv', [
      'First Name', 'Last Name', 'Email', 'Status', 'Role', 'Verified', 'Department', teamLabel, 'Team',
      'New Actions', 'Existing Actions', 'Total Actions', 'Points', 'CO2e lbs',
      'Water Saved (gallons)', 'Money Saved', 'Stories', 'Comments',
      'Hero Nominations Given', 'Hero Nominations Received',
      'Flags Filed', 'Flags Received', 'Joined', 'Registered'
    ], sortRows(report.users || [], this.state.userSort).map((r) => [
      r.firstName, r.lastName, r.email, r.status || '', r.level || '', r.verified ? 'yes' : 'no',
      r.department || '', r.division || '', r.teamName || '',
      r.newActions || 0, r.existingActions || 0, r.actionsCompleted, r.totalPoints, r.co2Lbs || 0,
      r.waterGallons || 0, this.moneySavedForCO2(r.co2Lbs),
      r.stories, r.comments, r.heroNominationsGiven, r.heroNominationsReceived,
      r.flagsFiled, r.flagsReceived, r.joinedAt || '', r.registeredAt || ''
    ]));
  }

  private exportCO2Savings = () => {
    const { report, co2View } = this.state;
    if (!report) return;
    const rows = co2View === 'department'
      ? (report.co2SavingsByDepartment || [])
      : (report.co2SavingsByAction || []);
    if (co2View === 'department') {
      downloadCsv('co2-savings-by-department.csv', [
        'Department', 'New Actions', 'Existing Actions', 'Total Actions',
        'New CO2e lbs', 'Existing CO2e lbs', 'Total CO2e lbs', 'Money Saved'
      ], rows.map((r) => [
        r.department || '', r.newCompletions, r.alreadyDoCompletions,
        (r.newCompletions || 0) + (r.alreadyDoCompletions || 0),
        r.newCO2Lbs, r.alreadyDoCO2Lbs, r.totalCO2Lbs, this.moneySavedForCO2(r.totalCO2Lbs)
      ]));
      return;
    }
    downloadCsv('co2-savings-by-action.csv', [
      'Action', 'Category', 'New Actions', 'Existing Actions', 'Total Actions',
      'New CO2e lbs', 'Existing CO2e lbs', 'Total CO2e lbs', 'Money Saved'
    ], rows.map((r) => [
      r.title || '', r.category || '', r.newCompletions, r.alreadyDoCompletions,
      (r.newCompletions || 0) + (r.alreadyDoCompletions || 0),
      r.newCO2Lbs, r.alreadyDoCO2Lbs, r.totalCO2Lbs, this.moneySavedForCO2(r.totalCO2Lbs)
    ]));
  }

  public render() {
    const { t } = this.props;
    const { loading, error, report, deptSort, divSort, userSort, co2View, pointsSeriesView } = this.state;
    const teamLabel = (report && report.labels && report.labels.teamSingular) || 'Division';
    const dailyActivity = (report && report.dailyActivity) || [];
    const activityBuckets = (report && report.activityBuckets) || [];
    const departments = (report && report.departments) || [];
    const divisions = (report && report.divisions) || [];
    const users = (report && report.users) || [];
    const popularActions = (report && report.popularActions) || [];
    const reconNotes = (report && report.reconciliation && report.reconciliation.notes) || [];
    const definitions = (report && report.definitions) || {};
    const maxDaily = dailyActivity.length
      ? Math.max.apply(null, dailyActivity.map((d) => d.actions).concat([1]))
      : 1;
    const co2Summary = report && report.co2SavingsSummary;
    const co2Rows = report
      ? (co2View === 'department'
        ? (report.co2SavingsByDepartment || [])
        : (report.co2SavingsByAction || []))
      : [];

    return (
      <div id="cc__admin_totals" className="cc__page">
        <div className="cc__admin_totals__wrap">
          <h1>{t('Totals')}</h1>
          {report && <p className="cc__admin_totals__lede">{report.publicName}</p>}
          {loading && <Spinner color="primary" />}
          {error && <Alert color="danger">{error}</Alert>}
          {report &&
            <div>
              <div className="cc__admin_totals__cards">
                {this.card('Pre-registered', report.summary.preregisteredParticipants || 0)}
                {this.card('Pre-registered, not joined', report.summary.preregisteredPendingJoin || 0)}
                {this.card('Joined participants', report.summary.totalParticipants)}
                {this.card('Active participants', report.summary.activeParticipants)}
                {this.card('Total actions', report.summary.totalActionsCompleted)}
                {this.card('Total points', report.summary.totalPoints)}
                {this.card('Avg points / participant', report.summary.avgPointsPerParticipant)}
                {this.card('Avg actions / participant', report.summary.avgActionsPerParticipant)}
                {this.card('Zero-action participants', report.summary.participantsWithZeroActions)}
                {this.card(`Participating ${teamLabel}s`, report.summary.participatingDivisions)}
                {this.card('Participating Departments', report.summary.participatingDepartments)}
                {this.card('CO2e savings (lbs)', report.summary.totalCO2Lbs)}
                {this.card('Electricity (kWh)', report.summary.totalElectricityKwh)}
                {this.card('Gas (gallons)', report.summary.totalGasGallons)}
              </div>
              <p className="cc__admin_totals__meta">
                Unique actions completed: {report.summary.uniqueActionsCompleted}
                {' · '}{report.queryCount} queries · {report.elapsedMs} ms
              </p>

              <h2>{t('Participation')}</h2>
              <ul className="cc__admin_totals__buckets">
                {activityBuckets.map((b) => <li key={b.label}>{b.label}: {b.display}</li>)}
              </ul>

              <div className="cc__admin_totals__tablehead">
                <h2>User summary</h2>
                <Button size="sm" color="secondary" onClick={this.exportUsers}>Download CSV</Button>
              </div>
              <p className="cc__admin_totals__meta">
                Joined accounts and pre-registrations ({users.length} rows). Admins can edit email, status,
                department, and {teamLabel.toLowerCase()}. Actions, points, stories, comments, hero nominations,
                and flags stay read-only.
              </p>
              {this.state.saveError && <Alert color="danger">{this.state.saveError}</Alert>}
              {this.userTable(sortRows(users, userSort), teamLabel, (key) => this.setState({ userSort: key }))}

              <div className="cc__admin_totals__tablehead">
                <h2>Department totals</h2>
                <Button size="sm" color="secondary" onClick={this.exportDepartments}>Download CSV</Button>
              </div>
              {this.orgTable(sortRows(departments, deptSort), false, (key) => this.setState({ deptSort: key }))}

              <div className="cc__admin_totals__tablehead">
                <h2>{`${teamLabel} totals`}</h2>
                <Button size="sm" color="secondary" onClick={this.exportDivisions}>Download CSV</Button>
              </div>
              {this.orgTable(sortRows(divisions, divSort), true, (key) => this.setState({ divSort: key }))}

              <div className="cc__admin_totals__tablehead">
                <h2>CO2 savings</h2>
                <div className="cc__admin_totals__co2controls">
                  <ButtonGroup size="sm">
                    <Button
                      color={co2View === 'department' ? 'primary' : 'secondary'}
                      outline={co2View !== 'department'}
                      onClick={() => this.setState({ co2View: 'department' })}
                    >
                      By Department
                    </Button>
                    <Button
                      color={co2View === 'action' ? 'primary' : 'secondary'}
                      outline={co2View !== 'action'}
                      onClick={() => this.setState({ co2View: 'action' })}
                    >
                      By Action
                    </Button>
                  </ButtonGroup>
                  <Button size="sm" color="secondary" onClick={this.exportCO2Savings}>Download CSV</Button>
                </div>
              </div>
              <p className="cc__admin_totals__meta">
                Only completed actions with CO2 savings.
                {co2Summary &&
                  <span>
                    {' '}New Actions: {co2Summary.newCompletions} / {co2Summary.newCO2Lbs} lbs
                    {' · '}Existing Actions: {co2Summary.alreadyDoCompletions} / {co2Summary.alreadyDoCO2Lbs} lbs
                    {' · '}Total: {co2Summary.totalCO2Lbs} lbs
                  </span>
                }
              </p>
              {this.co2Table(co2Rows, co2View)}

              <h2>Most popular actions</h2>
              <Table responsive={true} striped={true} size="sm">
                <thead>
                  <tr>
                    <th>Action</th><th>Category</th><th>Times completed</th>
                    <th>Unique participants</th><th>Total points awarded</th>
                  </tr>
                </thead>
                <tbody>
                  {popularActions.map((a) => (
                    <tr key={a.title}>
                      <td>{a.title}</td>
                      <td>{a.category}</td>
                      <td>{a.timesCompleted}</td>
                      <td>{a.uniqueParticipants}</td>
                      <td>{a.totalPointsAwarded}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <h2>Actions completed by day</h2>
              <div className="cc__admin_totals__chart">
                {dailyActivity.length === 0 && <p>No completions yet.</p>}
                {dailyActivity.map((d) => (
                  <div className="cc__admin_totals__barrow" key={d.day}>
                    <div className="cc__admin_totals__barlabel">{d.day}</div>
                    <div className="cc__admin_totals__bartrack">
                      <div className="cc__admin_totals__bar" style={{ width: `${Math.max(4, (d.actions / maxDaily) * 100)}%` }} />
                    </div>
                    <div className="cc__admin_totals__barval">{d.actions} / {d.points} pts</div>
                  </div>
                ))}
              </div>

              <div className="cc__admin_totals__tablehead">
                <h2>Total points over time</h2>
                <ButtonGroup size="sm">
                  <Button
                    color={pointsSeriesView === 'department' ? 'primary' : 'secondary'}
                    outline={pointsSeriesView !== 'department'}
                    onClick={() => this.setState({ pointsSeriesView: 'department' })}
                  >
                    By Department
                  </Button>
                  <Button
                    color={pointsSeriesView === 'division' ? 'primary' : 'secondary'}
                    outline={pointsSeriesView !== 'division'}
                    onClick={() => this.setState({ pointsSeriesView: 'division' })}
                  >
                    By {teamLabel}
                  </Button>
                </ButtonGroup>
              </div>
              <div className="cc__admin_totals__chart">
                {report.hourlyPointsTimeSeries &&
                  <HourlyPointsChart
                    block={report.hourlyPointsTimeSeries}
                    view={pointsSeriesView}
                    teamLabel={teamLabel}
                  />
                }
                {!report.hourlyPointsTimeSeries &&
                  <p className="cc__admin_totals__meta">Hourly points chart unavailable (update API).</p>
                }
              </div>

              <h2>Data notes</h2>
              <ul className="cc__admin_totals__notes">
                {reconNotes.map((n) => <li key={n}>{n}</li>)}
                {definitions.preregisteredParticipant &&
                  <li key="preregisteredParticipant">{definitions.preregisteredParticipant}</li>
                }
                {definitions.userSummary &&
                  <li key="userSummary">{definitions.userSummary}</li>
                }
                {definitions.co2SavingsTable &&
                  <li key="co2SavingsTable">{definitions.co2SavingsTable}</li>
                }
              </ul>
            </div>
          }
        </div>
        <Footer />
      </div>
    );
  }

  private card(label: string, value: number) {
    return (
      <div className="cc__admin_totals__card" key={label}>
        <div className="cc__admin_totals__cardlabel">{label}</div>
        <div className="cc__admin_totals__cardvalue">{value}</div>
      </div>
    );
  }

  private co2Table(rows: CO2SavingsRow[], view: 'department' | 'action') {
    if (!rows.length) {
      return <p className="cc__admin_totals__meta">No completed actions with CO2 savings yet.</p>;
    }
    return (
      <Table responsive={true} striped={true} size="sm">
        <caption className="cc__admin_totals__caption">
          {view === 'department' ? 'CO2 savings by department' : 'CO2 savings by action'}
        </caption>
        <thead>
          <tr>
            {view === 'department'
              ? <th scope="col">Department</th>
              : <React.Fragment><th scope="col">Action</th><th scope="col">Category</th></React.Fragment>
            }
            <th scope="col">New Actions</th>
            <th scope="col">Existing Actions</th>
            <th scope="col">New CO2e lbs</th>
            <th scope="col">Existing CO2e lbs</th>
            <th scope="col">Total CO2e lbs</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={view === 'department' ? r.department : String(r.actionId || r.title)}>
              {view === 'department'
                ? <td>{r.department}</td>
                : <React.Fragment><td>{r.title}</td><td>{r.category}</td></React.Fragment>
              }
              <td>{r.newCompletions}</td>
              <td>{r.alreadyDoCompletions}</td>
              <td>{r.newCO2Lbs}</td>
              <td>{r.alreadyDoCO2Lbs}</td>
              <td>{r.totalCO2Lbs}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  private rowKey = (r: UserRow) => (r.userId ? `u-${r.userId}` : `p-${r.email}`);

  private beginEdit = (r: UserRow) => {
    const { report } = this.state;
    let divisionEntityId = r.divisionEntityId || 0;
    if (!divisionEntityId && report && report.teamOptions && (r.department || r.division || r.teamName)) {
      const match = report.teamOptions.find((t) =>
        (r.divisionEntityId && t.entityId === r.divisionEntityId) ||
        (r.division && (t.division === r.division || t.name === r.division)) ||
        (r.teamName && t.name === r.teamName) ||
        (r.department && r.division &&
          t.department === r.department && t.division === r.division)
      );
      if (match) divisionEntityId = match.entityId;
    }
    this.setState({
      editingKey: this.rowKey(r),
      editDraft: {
        email: r.email || '',
        status: r.status || (r.preregistered ? 'pre-registered' : 'joined'),
        department: r.department || '',
        divisionEntityId
      },
      saveError: ''
    });
  }

  private cancelEdit = () => {
    this.setState({ editingKey: '', editDraft: null, saveError: '', saving: false });
  }

  private patchEditDraft = (patch: Partial<UserEditDraft>) => {
    const { editDraft } = this.state;
    if (!editDraft) return;
    this.setState({ editDraft: { ...editDraft, ...patch } });
  }

  private departmentOptions = (): string[] => {
    const { report } = this.state;
    if (!report) return [];
    const set: { [key: string]: boolean } = {};
    (report.teamOptions || []).forEach((t) => {
      if (t.department) set[t.department] = true;
    });
    (report.users || []).forEach((u) => {
      if (u.department) set[u.department] = true;
    });
    return Object.keys(set).sort((a, b) => a.localeCompare(b));
  }

  private divisionOptionsForDepartment = (department: string): TeamOption[] => {
    const { report } = this.state;
    const options = (report && report.teamOptions) || [];
    if (!department) return options.slice().sort((a, b) => a.name.localeCompare(b.name));
    return options
      .filter((t) => t.department === department)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private saveEdit = async (original: UserRow) => {
    const { apiBase, token, competitionName } = this.props;
    const { editDraft, report } = this.state;
    if (!editDraft || !report) return;

    const body: any = {
      userId: original.userId || 0,
      email: original.email
    };
    if (editDraft.email.trim() && editDraft.email.trim() !== original.email) {
      body.newEmail = editDraft.email.trim();
    }

    const selectedDivision = ((report.teamOptions || []) as TeamOption[])
      .find((t) => t.entityId === (editDraft.divisionEntityId || 0));

    if (original.userId > 0) {
      const originalStatus = original.status || (original.preregistered ? 'pre-registered + joined' : 'joined');
      if (editDraft.status !== originalStatus) {
        body.status = editDraft.status;
      }
      if ((editDraft.divisionEntityId || 0) !== (original.divisionEntityId || 0)) {
        body.divisionEntityId = editDraft.divisionEntityId || 0;
      }
      if (editDraft.department !== (original.department || '')) {
        body.department = editDraft.department;
      }
    } else {
      // Pre-registered: store Division as "Department - Division" (same as signup).
      if (selectedDivision) {
        const dept = selectedDivision.department || editDraft.department || '';
        const div = selectedDivision.division || selectedDivision.name || '';
        body.department = (dept && div && dept !== div)
          ? `${dept} - ${div}`
          : (selectedDivision.name || dept || div);
        body.divisionEntityId = selectedDivision.entityId;
      } else if (editDraft.department !== (original.department || '')) {
        body.department = editDraft.department;
      }
    }

    this.setState({ saving: true, saveError: '' });
    try {
      const response = await fetch(`${apiBase}/competitions/${competitionName}/admin/users`, {
        method: 'PATCH',
        headers: {
          'Authorization': token,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      if (!response.ok) {
        let message = 'Could not save user changes.';
        try {
          const errBody = await response.json();
          if (Array.isArray(errBody) && errBody[0] && errBody[0].message) {
            message = errBody[0].message;
          } else if (errBody && errBody.errors && errBody.errors[0] && errBody.errors[0].message) {
            message = errBody.errors[0].message;
          } else if (errBody && errBody.error) {
            message = errBody.error;
          }
        } catch (_parseErr) {
          // keep default
        }
        this.setState({ saving: false, saveError: message });
        return;
      }
      const payload = await response.json();
      const updated: UserRow = payload.user;
      const users = (report.users || []).map((u) => {
        if (this.rowKey(u) === this.rowKey(original)) {
          return updated;
        }
        // Email identity for prereg rows may change.
        if (!original.userId && u.email === original.email) {
          return updated;
        }
        return u;
      });
      this.setState({
        saving: false,
        editingKey: '',
        editDraft: null,
        report: { ...report, users }
      });
    } catch (_err) {
      this.setState({ saving: false, saveError: 'Network error saving user changes.' });
    }
  }

  private userTable(rows: UserRow[], teamLabel: string, onSort: (key: string) => void) {
    const currentSort = this.state.userSort;
    const { editingKey, editDraft, saving } = this.state;
    const departments = this.departmentOptions();
    const th = (label: string, key: string) => (
      <th key={key} scope="col" aria-sort={currentSort === key ? 'descending' : 'none'}>
        <button
          type="button"
          className="cc__admin_totals__sort"
          onClick={() => onSort(key)}
          aria-label={`Sort by ${label}${currentSort === key ? ', currently selected' : ''}`}
        >{label}</button>
      </th>
    );
    if (!rows.length) {
      return <p className="cc__admin_totals__meta">No joined users or pre-registrations yet.</p>;
    }
    return (
      <div className="cc__admin_totals__usertable">
        <Table responsive={true} striped={true} size="sm">
          <caption className="cc__admin_totals__caption">
            User summary (joined + pre-registered)
          </caption>
          <thead>
            <tr>
              {th('Name', 'lastName')}
              {th('Email', 'email')}
              {th('Status', 'status')}
              {th('Role', 'level')}
              {th('Department', 'department')}
              {th(teamLabel, 'division')}
              <th scope="col">Edit</th>
              {th('New Actions', 'newActions')}
              {th('Existing Actions', 'existingActions')}
              {th('Total Actions', 'actionsCompleted')}
              {th('Points', 'totalPoints')}
              {th('Stories', 'stories')}
              {th('Comments', 'comments')}
              {th('Hero given', 'heroNominationsGiven')}
              {th('Hero received', 'heroNominationsReceived')}
              {th('Flags filed', 'flagsFiled')}
              {th('Flags received', 'flagsReceived')}
              {th('CO2e lbs', 'co2Lbs')}
              {th('Water (gal)', 'waterGallons')}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const key = this.rowKey(r);
              const editing = editingKey === key && editDraft;
              const joined = r.userId > 0;
              const divisionChoices = editing
                ? this.divisionOptionsForDepartment(editDraft.department)
                : [];
              return (
                <tr key={key}>
                  <td>{[r.firstName, r.lastName].filter(Boolean).join(' ') || '—'}</td>
                  <td>
                    {editing ? (
                      <input
                        className="cc__admin_totals__input"
                        type="email"
                        value={editDraft.email}
                        disabled={saving}
                        onChange={(ev) => this.patchEditDraft({ email: ev.target.value })}
                      />
                    ) : r.email}
                  </td>
                  <td>
                    {editing ? (
                      joined ? (
                        <select
                          className="cc__admin_totals__input"
                          value={editDraft.status}
                          disabled={saving}
                          onChange={(ev) => this.patchEditDraft({ status: ev.target.value })}
                        >
                          <option value="joined">joined</option>
                          <option value="pre-registered + joined">pre-registered + joined</option>
                        </select>
                      ) : (
                        <span>pre-registered</span>
                      )
                    ) : (r.status || (r.preregistered ? 'pre-registered' : 'joined'))}
                  </td>
                  <td>{r.level || '—'}</td>
                  <td>
                    {editing ? (
                      <select
                        className="cc__admin_totals__input"
                        value={editDraft.department}
                        disabled={saving}
                        onChange={(ev) => {
                          const department = ev.target.value;
                          const nextDivisions = this.divisionOptionsForDepartment(department);
                          const keepDivision = nextDivisions.some((d) => d.entityId === editDraft.divisionEntityId);
                          this.patchEditDraft({
                            department,
                            divisionEntityId: keepDivision ? editDraft.divisionEntityId : 0
                          });
                        }}
                      >
                        <option value="">—</option>
                        {departments.map((d) => <option key={d} value={d}>{d}</option>)}
                      </select>
                    ) : (r.department || '—')}
                  </td>
                  <td>
                    {editing ? (
                      <select
                        className="cc__admin_totals__input"
                        value={editDraft.divisionEntityId || ''}
                        disabled={saving || (!editDraft.department && divisionChoices.length === 0)}
                        onChange={(ev) => {
                          const divisionEntityId = Number(ev.target.value) || 0;
                          const selected = divisionChoices.find((d) => d.entityId === divisionEntityId);
                          this.patchEditDraft({
                            divisionEntityId,
                            department: selected && selected.department
                              ? selected.department
                              : editDraft.department
                          });
                        }}
                      >
                        <option value="">
                          {editDraft.department
                            ? (divisionChoices.length ? '— Select division —' : 'No divisions in this department')
                            : '— Select department first —'}
                        </option>
                        {divisionChoices.map((d) => (
                          <option key={d.entityId} value={d.entityId}>
                            {d.division || d.name}
                          </option>
                        ))}
                      </select>
                    ) : (r.division || r.teamName || '—')}
                  </td>
                  <td>
                    {editing ? (
                      <div className="cc__admin_totals__editactions">
                        <Button size="sm" color="primary" disabled={saving} onClick={() => this.saveEdit(r)}>
                          {saving ? 'Saving…' : 'Save'}
                        </Button>
                        <Button size="sm" color="secondary" outline={true} disabled={saving} onClick={this.cancelEdit}>
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button size="sm" color="link" className="cc__admin_totals__editlink" onClick={() => this.beginEdit(r)}>
                        Edit
                      </Button>
                    )}
                  </td>
                  <td>{r.newActions || 0}</td>
                  <td>{r.existingActions || 0}</td>
                  <td>{r.actionsCompleted}</td>
                  <td>{r.totalPoints}</td>
                  <td>{r.stories}</td>
                  <td>{r.comments}</td>
                  <td>{r.heroNominationsGiven}</td>
                  <td>{r.heroNominationsReceived}</td>
                  <td>{r.flagsFiled}</td>
                  <td>{r.flagsReceived}</td>
                  <td>{r.co2Lbs || 0}</td>
                  <td>{r.waterGallons || 0}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  }

  private orgTable(rows: OrgRow[], withDivision: boolean, onSort: (key: string) => void) {
    const currentSort = withDivision ? this.state.divSort : this.state.deptSort;
    const th = (label: string, key: string) => (
      <th key={key} scope="col" aria-sort={currentSort === key ? 'descending' : 'none'}>
        <button
          type="button"
          className="cc__admin_totals__sort"
          onClick={() => onSort(key)}
          aria-label={`Sort by ${label}${currentSort === key ? ', currently selected' : ''}`}
        >{label}</button>
      </th>
    );
    return (
      <Table responsive={true} striped={true} size="sm">
        <caption className="cc__admin_totals__caption">
          {withDivision ? 'Division totals' : 'Department totals'}
        </caption>
        <thead>
          <tr>
            {withDivision && th('Rank', 'rank')}
            {th('Department', 'department')}
            {withDivision && th('Division', 'division')}
            {th('Participants', 'participants')}
            {th('New Actions', 'newActions')}
            {th('Existing Actions', 'existingActions')}
            {th('Total Actions-to-Date', 'actionsCompleted')}
            {th('Total Points', 'totalPoints')}
            {th('New Actions CO2e lbs', 'newCO2Lbs')}
            {th('Existing Actions CO2e lbs', 'existingCO2Lbs')}
            {th('Total Actions CO2e lbs', 'co2Lbs')}
            {th('Total Actions Water (gal)', 'waterGallons')}
            {th('Total Actions $ Saved', 'moneySaved')}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.department}-${r.division || ''}`}>
              {withDivision && <td>{r.rank}</td>}
              <td>{r.department}</td>
              {withDivision && <td>{r.division}</td>}
              <td>{r.participants}</td>
              <td>{r.newActions || 0}</td>
              <td>{r.existingActions || 0}</td>
              <td>{r.actionsCompleted}</td>
              <td>{r.totalPoints}</td>
              <td>{r.newCO2Lbs || 0}</td>
              <td>{r.existingCO2Lbs || 0}</td>
              <td>{r.co2Lbs || 0}</td>
              <td>{r.waterGallons || 0}</td>
              <td>{this.moneySavedForCO2(r.co2Lbs)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }
}

const mapStateToProps = (state: State): Props => ({
  apiBase: userApiBasePath(state),
  token: state.token || '',
  competitionName: (state.competition && state.competition.name) || selectors.competitionName(state) || '',
  canManage: selectors.canViewAdminTools(state),
  competitionPledges: state.competitionPledges
});

export default asPage({ requiresCompetitionStart: false, authenticated: true })(compose(
  withLocalization('account'),
  connect(mapStateToProps)
)(TotalsPage));
